import { Card, Container, Group, SegmentedControl, Select, Stack, Switch, Text, Title } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useStore } from "../store";
import { usePlan } from "../hooks/usePlan";
import { HOME_PAGE_OPTIONS } from "../homePage";

/**
 * Personal preferences — per-user (and per-device, via the localStorage-backed store).
 * Nothing here is admin-gated: every user sets their own.
 *
 *  - Default page: which page "/" lands on ("auto" = the plan default).
 *  - Contact lists: default sorting + name format. Sorting and rendering are coupled:
 *    alphabetical is always by last name, and alphabetically sorted lists render
 *    "Last, First" regardless of the name format, so the order is visible.
 *  - Activity feed: an opt-out for users who don't want the team feed in their nav.
 *    The tenant keeps the feature; this only hides it for this user.
 */
export default function PreferencesPage() {
  const { t } = useTranslation("Veft");
  const { canUseCollaboration } = usePlan();

  const homePage = useStore((s) => s.homePage);
  const setHomePage = useStore((s) => s.setHomePage);
  const hideActivity = useStore((s) => s.hideActivity);
  const setHideActivity = useStore((s) => s.setHideActivity);
  const sortMode = useStore((s) => s.sortMode);
  const setSortMode = useStore((s) => s.setSortMode);
  const nameFormat = useStore((s) => s.nameFormat);
  const setNameFormat = useStore((s) => s.setNameFormat);

  const showActivity = canUseCollaboration && !hideActivity;

  const pageLabels = {
    activity: t("nav_activity"),
    candidates: t("nav_candidates"),
    tasks: t("nav_tasks"),
    projects: t("nav_projects"),
    clients: t("nav_clients"),
    search: t("nav_search"),
    documents: t("nav_documents"),
  };

  const homeOptions = [
    { value: "auto", label: t("home_page_auto") },
    ...HOME_PAGE_OPTIONS
      // Activity is only a valid landing page when it's actually shown.
      .filter((p) => p !== "activity" || showActivity)
      .map((p) => ({ value: p, label: pageLabels[p] })),
  ];

  // Coerce a saved value that's no longer reachable (e.g. "activity" after hiding it)
  // back to "auto" for display — matches what resolveHomePath does at runtime.
  const homeValue = homeOptions.some((o) => o.value === homePage) ? homePage : "auto";

  function onToggleActivity(visible) {
    const hide = !visible;
    setHideActivity(hide);
    // Hiding the feed makes it an invalid landing page — revert the choice to auto.
    if (hide && homePage === "activity") setHomePage("auto");
  }

  return (
    <Container size="md" py="xl">
      <Title order={2} mb="xs">{t("preferences_title")}</Title>
      <Text size="sm" c="dimmed" mb="lg">{t("preferences_subtitle")}</Text>

      <Stack gap="md">
        <Card withBorder>
          <Stack gap="sm">
            <Title order={5}>{t("pref_home_title")}</Title>
            <Text size="sm" c="dimmed">{t("pref_home_help")}</Text>
            <Select
              data={homeOptions}
              value={homeValue}
              onChange={(v) => v && setHomePage(v)}
              allowDeselect={false}
              w={240}
            />
          </Stack>
        </Card>

        <Card withBorder>
          <Stack gap="sm">
            <Title order={5}>{t("pref_lists_title")}</Title>
            <Text size="sm" c="dimmed">{t("pref_lists_help")}</Text>
            <Group gap="xl" wrap="wrap">
              <Stack gap={4}>
                <Text size="sm" fw={500}>{t("sort_label")}</Text>
                <SegmentedControl
                  value={sortMode}
                  onChange={setSortMode}
                  data={[
                    { value: "recent", label: t("sort_recent") },
                    { value: "alpha", label: t("sort_alpha") },
                  ]}
                />
              </Stack>
              <Stack gap={4}>
                <Text size="sm" fw={500}>{t("name_format_label")}</Text>
                <SegmentedControl
                  value={nameFormat}
                  onChange={setNameFormat}
                  data={[
                    { value: "firstLast", label: t("name_format_first_last") },
                    { value: "lastFirst", label: t("name_format_last_first") },
                  ]}
                />
              </Stack>
            </Group>
          </Stack>
        </Card>

        {canUseCollaboration && (
          <Card withBorder>
            <Stack gap="sm">
              <Title order={5}>{t("pref_activity_title")}</Title>
              <Text size="sm" c="dimmed">{t("pref_activity_help")}</Text>
              <Switch
                label={t("pref_show_activity")}
                checked={!hideActivity}
                onChange={(e) => onToggleActivity(e.currentTarget.checked)}
              />
            </Stack>
          </Card>
        )}
      </Stack>
    </Container>
  );
}
